import {
  Alert,
  Button,
  Col,
  Form,
  Input,
  message,
  Modal,
  Row,
  Spin,
  Select,
} from "antd";
import React, { useEffect, useState, useRef } from "react";
import { LoadingOutlined } from "@ant-design/icons";
import SimpleReactValidator from "simple-react-validator";
import UserServices from "../../services/UserServices";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { translate } from "../../locale";

const NewNote = ({ username, visible, onComplete, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(false);
  const [values, setValues] = useState({
    patient: username,
    subscription_id: "",
    title: "",
    body: "",
  });
  const [subscriptions, setSubscriptions] = useState([]);
  const [error, setError] = useState("");
  const [, forceUpdate] = useState();

  const simpleValidator = useRef(
    new SimpleReactValidator({
      autoForceUpdate: { forceUpdate: forceUpdate },
      element: (message) => (
        <div className="text-red-600 pt-2 text-xs">{message}</div>
      ),
      messages: {
        required: "Campo requerido",
        title: "El título es requerido",
        message: "El mensaje es requerido",
      },
    })
  );

  useEffect(() => {
    if (visible !== null) {
      setActive(visible);
    }
  }, [visible]);

  useEffect(() => {
    getSubscriptions();
  }, []);

  const disabled = () => !simpleValidator.current.allValid();

  const getSubscriptions = async () => {
    try {
      setLoading(true);
      const response = await UserServices.getSubscription(username);
      if (response.status >= 200 && response.status < 300) {
        setSubscriptions(
          response.data.subscriptions.map((i) => ({
            value: i.id,
            label: i.name,
          }))
        );
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  };

  const onFinish = async () => {
    setLoading(true);
    setError("");
    if (!simpleValidator.current.allValid()) {
      simpleValidator.current.showMessages();
      forceUpdate();
      setError(translate("El {0} ingresado no es válido", translate("título")));
      return;
    }
    try {
      const data = {
        patient: values.patient,
        subscription_id: values.subscription_id,
        title: values.title,
        body: values.body,
      };

      const res = await UserServices.newNote(data);
      resetForm();

      if (res.status >= 200 && res.status < 300) {
        setLoading(false);

        message.success(
          translate("La {0} se guardó exitosamente.", translate("nota"))
        );
        if (onComplete) {
          onComplete();
        }
      } else {
        setError(translate("No se pudieron guardar sus datos."));
      }
    } catch (e) {
      console.log(e);
      setError(e.message);
      setLoading(false);
    }
  };

  const resetForm = () => {
    setValues({
      patient: username,
      subscription_id: "",
      title: "",
      body: "",
    });
    simpleValidator.current.hideMessages();
    simpleValidator.current.visibleFields = [];
    forceUpdate();
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((v) => ({ ...v, [name]: value }));
  };
  const handleBlur = (e) => {
    const { name, value } = e.target;
    simpleValidator.current.showMessageFor(name);
    setValues((v) => ({ ...v, [name]: value }));
  };

  const handleChangeQuill = (name, value) => {
    if (name === "body" && value === "<p><br></p>") {
      value = "";
    }
    setValues((v) => ({
      ...v,
      [name]: value.replace(/ /g, "&nbsp;"),
    }));
  };
  const handleBlurQuill = (name, value) => {
    console.log("blur", name, value);
    simpleValidator.current.showMessageFor(name);
  };

  const handleCancel = () => {
    console.log("Clicked cancel button");
    setActive(false);
    resetForm();
    if (onCancel) {
      onCancel();
    }
  };
  return (
    <Modal
      open={active}
      title={translate("Nueva nota")}
      onOk={null}
      onCancel={handleCancel}
      footer={false}
    >
      {error && (
        <Alert
          message={error}
          type="error"
          className="mb-6"
          closable
          onClose={() => setError("")}
        />
      )}
      <Form
        initialValues={{ remember: true }}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        autoComplete="off"
        className="w-full"
      >
        <Form.Item>
          <Select
            name="subscription_id"
            onChange={(value) => {
              setValues((v) => ({ ...v, subscription_id: value }));
            }}
            value={values.subscription_id}
          >
            <Select.Option value="">Seleccione suscripción</Select.Option>
            {subscriptions.map((item, key) => (
              <Select.Option key={key} value={item.value}>
                {item.label}
              </Select.Option>
            ))}
          </Select>
          {simpleValidator.current.message(
            "subscription_id",
            values.subscription_id,
            "required"
          )}
        </Form.Item>
        <Form.Item>
          <Input
            placeholder={translate("Título")}
            className="border-custom_primary"
            name="title"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.title}
          />
          {simpleValidator.current.message("title", values.title, "required")}
        </Form.Item>
        <Form.Item>
          <ReactQuill
            theme="snow"
            value={values.body}
            modules={{
              toolbar: [
                [{ header: [1, 2, 3, 4, 5, 6, false] }],
                ["bold", "italic", "underline", "strike", "blockquote"],
                [
                  { list: "ordered" },
                  { list: "bullet" },
                  { indent: "-1" },
                  { indent: "+1" },
                ],
                ["link", "image"],
                [{ color: [] }, { background: [] }],
                ["clean"],
              ],
            }}
            formats={[
              "header",
              "bold",
              "italic",
              "underline",
              "strike",
              "blockquote",
              "list",
              "bullet",
              "indent",
              "link",
              "image",
            ]}
            onChange={(e) => handleChangeQuill("body", e)}
            onBlur={(e) => handleBlurQuill("body", e)}
            className="ql-container-format-1"
          />
          {simpleValidator.current.message("body", values.body, "required")}
        </Form.Item>
        <Form.Item>
          <Row align="end" gutter={16}>
            <Col>
              <Button key="back" onClick={handleCancel}>
                Cancelar
              </Button>
            </Col>
            <Col>
              <Button
                disabled={disabled()}
                htmlType="submit"
                type="primary"
                className="bg-custom_green border-custom_green hover:bg-custom_primary hover:border-custom_primary"
              >
                {loading ? (
                  <Spin
                    indicator={<LoadingOutlined style={{ color: "#666666" }} />}
                  />
                ) : (
                  "Enviar"
                )}
              </Button>
            </Col>
          </Row>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default NewNote;
